import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  MapPin,
  Monitor,
  CheckCircle,
  XCircle,
  ShieldAlert,
  RefreshCw,
  Globe,
  Smartphone,
} from 'lucide-react';
import { format } from 'date-fns';
import { getClientSignInLogs, SignInLogEntry } from '../../services/api';

interface UserSignInHistoryProps {
  clientId: string;
  userId: string;
  userDisplayName: string;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// Get severity color for anomaly badges
function getAnomalySeverityColor(severity: string): string {
  switch (severity) {
    case 'high':
      return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    case 'low':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
    default:
      return 'bg-muted text-foreground';
  }
}

// Get risk level color
function getRiskLevelColor(riskLevel: string): string {
  switch (riskLevel) {
    case 'high':
      return 'text-red-600 dark:text-red-400';
    case 'medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'low':
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-muted-foreground';
  }
}

// Format anomaly type for display
function formatAnomalyType(type: string): string {
  const labels: Record<string, string> = {
    'new_location': 'New Location',
    'impossible_travel': 'Impossible Travel',
    'failed_login_spike': 'Failed Login',
    'new_device': 'New Device',
    'off_hours': 'Off Hours',
    'risky_signin': 'Risky Sign-In',
    'new_app': 'New App',
  };
  return labels[type] || type;
}

export default function UserSignInHistory({ clientId, userId, userDisplayName: _userDisplayName }: UserSignInHistoryProps) {
  const [logs, setLogs] = useState<SignInLogEntry[]>([]);
  const [summary, setSummary] = useState<{
    totalLogs: number;
    failedLogins: number;
    riskySignIns: number;
    anomaliesDetected: number;
    uniqueLocations: number;
    uniqueDevices: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllLogs, setShowAllLogs] = useState(false);

  useEffect(() => {
    loadSignInLogs();
  }, [clientId, userId]);

  async function loadSignInLogs() {
    setLoading(true);
    setError(null);
    try {
      const result = await getClientSignInLogs(clientId, {
        userId,
        top: 100,
        includeNonInteractive: false,
      });

      if (result.success) {
        setLogs(result.logs);
        setSummary(result.summary);
      } else {
        setError(result.error || 'Failed to load sign-in logs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sign-in logs');
    } finally {
      setLoading(false);
    }
  }

  const displayedLogs = showAllLogs ? logs : logs.slice(0, 10);
  const logsWithAnomalies = logs.filter(l => l.anomalies && l.anomalies.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
        <button
          onClick={loadSignInLogs}
          className="mt-3 text-sm text-red-600 dark:text-red-400 hover:underline inline-flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-foreground">{summary.totalLogs}</div>
            <div className="text-xs text-muted-foreground">Total Sign-Ins</div>
          </div>
          <div className={classNames(
            'p-3 rounded-lg',
            summary.failedLogins > 0
              ? 'bg-red-50 dark:bg-red-900/20'
              : 'bg-muted'
          )}>
            <div className={classNames(
              'text-2xl font-bold',
              summary.failedLogins > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-foreground'
            )}>
              {summary.failedLogins}
            </div>
            <div className="text-xs text-muted-foreground">Failed Logins</div>
          </div>
          <div className={classNames(
            'p-3 rounded-lg',
            summary.anomaliesDetected > 0
              ? 'bg-yellow-50 dark:bg-yellow-900/20'
              : 'bg-muted'
          )}>
            <div className={classNames(
              'text-2xl font-bold',
              summary.anomaliesDetected > 0
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-foreground'
            )}>
              {summary.anomaliesDetected}
            </div>
            <div className="text-xs text-muted-foreground">Anomalies</div>
          </div>
        </div>
      )}

      {/* Anomalies Section */}
      {logsWithAnomalies.length > 0 && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 flex items-center mb-2">
            <ShieldAlert className="h-4 w-4 mr-1" />
            Detected Anomalies ({logsWithAnomalies.length})
          </h4>
          <div className="space-y-2">
            {logsWithAnomalies.slice(0, 5).map((log) => (
              <div key={log.id} className="text-sm">
                <div className="flex flex-wrap gap-1 mb-1">
                  {log.anomalies?.map((anomaly, idx) => (
                    <span
                      key={idx}
                      className={classNames(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        getAnomalySeverityColor(anomaly.severity)
                      )}
                      title={anomaly.description}
                    >
                      {formatAnomalyType(anomaly.type)}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  {format(new Date(log.timestamp), 'MMM d, h:mm a')} - {log.anomalies?.[0]?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign-In Logs Table */}
      <div className="overflow-hidden">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium text-foreground">
            Recent Sign-Ins
          </h4>
          <button
            onClick={loadSignInLogs}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </button>
        </div>

        {logs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">
            No sign-in logs found for this user
          </p>
        ) : (
          <>
            <div className="divide-y divide-border">
              {displayedLogs.map((log) => (
                <div key={log.id} className="py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Status indicator and timestamp */}
                      <div className="flex items-center gap-2 mb-1">
                        {log.status.errorCode === 0 ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium text-foreground">
                          {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                        </span>
                        {log.riskLevel !== 'none' && (
                          <span className={classNames('text-xs font-medium', getRiskLevelColor(log.riskLevel))}>
                            {log.riskLevel.toUpperCase()} RISK
                          </span>
                        )}
                        {log.anomalies && log.anomalies.length > 0 && (
                          <div className="flex gap-1">
                            {log.anomalies.map((a, i) => (
                              <span
                                key={i}
                                className={classNames(
                                  'inline-flex items-center px-1.5 py-0.5 rounded text-xs',
                                  getAnomalySeverityColor(a.severity)
                                )}
                                title={a.description}
                              >
                                {formatAnomalyType(a.type)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {/* Location */}
                        <span className="inline-flex items-center">
                          <MapPin className="h-3.5 w-3.5 mr-1" />
                          {[log.location.city, log.location.state, log.location.countryOrRegion]
                            .filter(Boolean)
                            .join(', ') || 'Unknown location'}
                        </span>

                        {/* IP Address */}
                        <span className="inline-flex items-center">
                          <Globe className="h-3.5 w-3.5 mr-1" />
                          {log.ipAddress}
                        </span>

                        {/* Device */}
                        <span className="inline-flex items-center">
                          <Monitor className="h-3.5 w-3.5 mr-1" />
                          {[log.deviceDetail.browser, log.deviceDetail.operatingSystem]
                            .filter(Boolean)
                            .join(' on ') || 'Unknown device'}
                        </span>

                        {/* App */}
                        <span className="inline-flex items-center">
                          <Smartphone className="h-3.5 w-3.5 mr-1" />
                          {log.appDisplayName}
                        </span>
                      </div>

                      {/* Error message if failed */}
                      {log.status.errorCode !== 0 && log.status.failureReason && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {log.status.failureReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Show more button */}
            {logs.length > 10 && (
              <button
                onClick={() => setShowAllLogs(!showAllLogs)}
                className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {showAllLogs ? 'Show less' : `Show all ${logs.length} sign-ins`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Location & Device Summary */}
      {summary && (summary.uniqueLocations > 1 || summary.uniqueDevices > 1) && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">{summary.uniqueLocations}</span> unique location{summary.uniqueLocations !== 1 ? 's' : ''}
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">{summary.uniqueDevices}</span> unique device{summary.uniqueDevices !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
